// NOTE: keep in sync with backend values in rate_converter.rb and possibly other places
// NOTE 2: make sure these are floats
import { RateMode } from 'types'
import { roundMonetaryValue } from 'services/formatting'

export const HOURS_PER_DAY = 8.0
export const HOURS_PER_MONTH = 8.0 * 22
export const HOURS_PER_YEAR = 8.0 * 22 * 12

function getHourlyRateMultiplier(rateMode: RateMode) {
  switch (rateMode) {
    case 'year':
      return HOURS_PER_YEAR

    case 'month':
      return HOURS_PER_MONTH

    case 'day':
      return HOURS_PER_DAY
  }

  // Assume hourly if it doesn't match any of the above cases
  return 1
}

/**
 * Get the hourly rate that matches the given rate and rate mode
 * @param rate the rate to convert
 * @param rateMode the RateMode of the given rate
 * @param options.round set to true to round the resulting hourly rate
 */
export function getHourlyRate(rate: number, rateMode: RateMode, options: { round?: boolean } = {}): number {
  if (!rate || !rateMode || rateMode === 'hour') return rate

  const hourlyRate = rate / getHourlyRateMultiplier(rateMode)

  // If rounding is enabled, we round the value to an integer (we remove the cents)
  if (options?.round) return Math.round(hourlyRate)

  // If rounding is disabled, we round the value to the cents (removes digits from the third decimal on)
  return roundMonetaryValue(hourlyRate)
}

/**
 * Convert a numeric rate between different rate modes
 *
 * @param rate the numeric rate to convert
 * @param fromRateMode the RateMode that the given rate is in
 * @param toRateMode the RateMode that you want the return value to be in
 * @param options.round set to true to round the final result into an integer
 * @param options.roundHourlyRate set to true to round the hourly rate during the conversion
 */
export function convertRateMode(
  rate: number,
  fromRateMode: RateMode,
  toRateMode: RateMode,
  options: { roundHourlyRate?: boolean, round?: boolean } = {},
) {
  if (fromRateMode === toRateMode) return rate

  const hourlyRate = getHourlyRate(rate, fromRateMode, { round: options?.roundHourlyRate })
  const convertedRate = hourlyRate * getHourlyRateMultiplier(toRateMode)

  if (options?.round) return Math.round(convertedRate)

  return convertedRate
}
